// SellerFacets — the Seller / Contact / Agent-represents filters and the card's seller line (core 1681 + 1685).
//
// Owner 2026-09-21: where an advert is listed (Sources), whose property it is (SELLER) and who handles it (CONTACT)
// are three separate questions — never one list mixing Developer / Agent / Owner. An agent is ALWAYS a
// representative. An unknown principal stays unknown (never defaulted to "private owner"). Inferred values count in
// the filters and are labelled "likely" on the card — nobody confirms owner/agent, it is read from how many adverts
// the author has. Filter semantics (server): within one filter the ticks OR, across filters AND on the same advert,
// and a card passes if any of its live adverts does.
import React, { useEffect, useState } from 'react'
import { getSellerCompanies } from '../../db/core.js'

export const SELLER_KINDS = ['private_owner', 'developer', 'bank', 'other_company', 'unknown']
export const CONTACT_ROLES = ['owner_direct', 'agent', 'company_rep', 'unknown']
// the principals an agent can act for — the seller kinds, without which a representative makes no sense
export const AGENT_FOR = ['private_owner', 'developer', 'bank', 'other_company', 'unknown']

// Named companies (developers, banks) — fetched once per page load and shared; a chip or a row that renders before
// the list arrives falls back to the kind's word, never to a raw id.
let companiesCache = null
let companiesPromise = null
const listeners = new Set()
function loadCompanies() {
  if (companiesCache || companiesPromise) return companiesPromise
  companiesPromise = getSellerCompanies()
    .then(rows => { companiesCache = rows; listeners.forEach(fn => fn(rows)) })
    .catch(e => { companiesPromise = null; console.error('[seller-companies]', e.message) })
  return companiesPromise
}
export function useSellerCompanies() {
  const [rows, setRows] = useState(companiesCache || [])
  useEffect(() => {
    if (companiesCache) { setRows(companiesCache); return undefined }
    listeners.add(setRows)
    loadCompanies()
    return () => { listeners.delete(setRows) }
  }, [])
  return rows
}
export function sellerCompanyName(id) {
  return companiesCache?.find(c => c.party_id === id)?.name || null
}

const toggleIn = (arr, v) => ((arr || []).includes(v) ? (arr || []).filter(x => x !== v) : [...(arr || []), v])

function Row({ on, label, onClick, indent = false, hint }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} title={hint}
      className={`w-full flex items-center gap-2 text-sm py-0.5 text-left max-md:min-h-11 ${indent ? 'pl-5' : ''} ${on ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
      <span className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center text-[10px] ${on ? 'border-primary bg-primary/15 text-primary' : 'border-input'}`}>{on ? '✓' : ''}</span>
      <span className="flex-1 truncate">{label}</span>
    </button>
  )
}

// SELLER — whose property. Developer and Bank open their named companies underneath (a named tick is its own
// value, OR-ed with the kinds: "Private owner + Archi" means either).
export function SellerFacet({ filters, setFilters, t }) {
  const companies = useSellerCompanies()
  const kinds = filters.sellerKinds || [], ids = filters.sellerIds || []
  const setKinds = v => setFilters(f => ({ ...f, sellerKinds: toggleIn(f.sellerKinds, v) }))
  const setId = id => setFilters(f => ({ ...f, sellerIds: toggleIn(f.sellerIds, id) }))
  const named = kind => companies.filter(c => c.company_kind === kind)
  return (
    <div className="space-y-0.5">
      {SELLER_KINDS.map(k => (
        <React.Fragment key={k}>
          <Row on={kinds.includes(k)} label={t('desk_sellerkind_' + k)} onClick={() => setKinds(k)}
            hint={k === 'private_owner' ? t('desk_seller_likely_note') : undefined} />
          {(k === 'developer' || k === 'bank') && named(k).map(c => (
            <Row key={c.party_id} indent on={ids.includes(c.party_id)} label={c.name} onClick={() => setId(c.party_id)} />
          ))}
        </React.Fragment>
      ))}
      <p className="text-[11px] leading-snug text-muted-foreground pt-1">{t('desk_seller_likely_note')}</p>
    </div>
  )
}

// CONTACT — who handles it. "Agent represents" opens under Agent only: it answers nothing about an owner or a
// company's own sales team.
export function ContactFacet({ filters, setFilters, t }) {
  const roles = filters.contactRoles || [], forWhom = filters.agentRepresents || []
  const agentOn = roles.includes('agent')
  return (
    <div className="space-y-0.5">
      {CONTACT_ROLES.map(r => (
        <React.Fragment key={r}>
          <Row on={roles.includes(r)} label={t('desk_contactrole_' + r)}
            onClick={() => setFilters(f => {
              const next = toggleIn(f.contactRoles, r)
              // un-ticking Agent drops its sub-filter too — an invisible "acting for" must not survive its parent
              return { ...f, contactRoles: next, ...(r === 'agent' && !next.includes('agent') ? { agentRepresents: [] } : {}) }
            })} />
          {r === 'agent' && agentOn && (
            <div className="pl-5 pt-0.5">
              <div className="text-[11px] text-muted-foreground">{t('desk_facet_agent_represents')}</div>
              {AGENT_FOR.map(k => (
                <Row key={k} on={forWhom.includes(k)} label={t('desk_sellerkind_' + k)}
                  onClick={() => setFilters(f => ({ ...f, agentRepresents: toggleIn(f.agentRepresents, k) }))} />
              ))}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

// Chips for the search box — one per tick, each removable on its own.
export function sellerChips(f, t) {
  const out = []
  for (const v of (f.sellerKinds || [])) out.push({ key: `sellerKind:${v}`, label: `${t('desk_facet_seller_whose')}: ${t('desk_sellerkind_' + v)}`, arrayKey: 'sellerKinds', value: v, editKey: 'sellerKinds' })
  for (const v of (f.sellerIds || [])) out.push({ key: `sellerId:${v}`, label: `${t('desk_facet_seller_whose')}: ${sellerCompanyName(v) || t('desk_sellerkind_other_company')}`, arrayKey: 'sellerIds', value: v, editKey: 'sellerIds' })
  for (const v of (f.contactRoles || [])) out.push({ key: `contactRole:${v}`, label: `${t('desk_facet_contact')}: ${t('desk_contactrole_' + v)}`, arrayKey: 'contactRoles', value: v, editKey: 'contactRoles' })
  for (const v of (f.agentRepresents || [])) out.push({ key: `agentFor:${v}`, label: `${t('desk_seller_agent_for')}: ${t('desk_sellerkind_' + v)}`, arrayKey: 'agentRepresents', value: v, editKey: 'agentRepresents' })
  return out
}

// The card's one line: "Seller: Bank of Georgia · Agent: Giorgi X (acting for a bank)". Inferred values say "likely".
export function sellerLine(s, t) {
  if (!s) return null
  const likely = strength => (strength === 'inferred' ? ` (${t('desk_seller_likely')})` : '')
  // an owner selling directly IS the private owner — saying both repeats one fact (owner 2026-09-21: shorten it)
  const seller = s.seller_kind === 'unknown' || (s.seller_kind === 'private_owner' && s.contact_role === 'owner_direct') ? null
    : `${t('desk_facet_seller_whose')}: ${s.seller_name || t('desk_sellerkind_' + s.seller_kind)}${likely(s.seller_strength)}`
  let contact = null
  if (s.contact_role === 'agent') {
    contact = `${t('desk_contactrole_agent')}${s.representative_name ? `: ${s.representative_name}` : ''}${likely(s.contact_strength)}`
    if (s.agent_represents && s.agent_represents !== 'unknown') contact += ` · ${t('desk_seller_agent_for')}: ${t('desk_sellerkind_' + s.agent_represents)}`
  } else if (s.contact_role === 'owner_direct') contact = `${t('desk_contactrole_owner_direct')}${likely(s.contact_strength)}`
  else if (s.contact_role === 'company_rep') contact = t('desk_contactrole_company_rep')
  return [seller, contact].filter(Boolean).join(' · ') || null
}
